<!-- ═══════════════ YIELD FARMING PAGE ═══════════════ -->
<section class="max-w-[1320px] mx-auto px-6 lg:px-10 pb-16 pt-8 animate-fade-in">

  <!-- Header Section -->
  <!-- Header Section -->
  <div class="mb-8">
    <h1 class="font-display font-black text-[36px] md:text-[44px] text-white tracking-wide leading-tight mb-3">
      Yield <span class="text-gold">Farms</span>
    </h1>
    <div class="mt-6 mb-8 p-5 rounded-xl bg-gold/10 border border-gold/30 flex items-start gap-3 w-full">
      <svg class="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div class="flex-1">
        <p class="text-white text-[14.5px] leading-relaxed mb-4">
          <span class="font-bold text-gold">Important Note:</span> Yield farming starts after you have become an LP. When you yield farm, you take those LP tokens you just received and deposit them into a separate "farming" or "reward" contract on the DeFi platform.
        </p>
        <ul class="space-y-3">
          <li class="flex items-start gap-2.5">
            <span class="text-gold text-[16px] leading-none select-none shrink-0 mt-0.5">•</span>
            <span class="text-white text-[13.5px] leading-relaxed">
              <strong class="text-gold font-bold">What you earn:</strong> In addition to the standard trading fees, you now earn extra bonus rewards, almost always paid out in the platform's own native cryptocurrency (e.g., CAKE on PancakeSwap, or UNI on Uniswap).
            </span>
          </li>
          <li class="flex items-start gap-2.5">
            <span class="text-gold text-[16px] leading-none select-none shrink-0 mt-0.5">•</span>
            <span class="text-white text-[13.5px] leading-relaxed">
              <strong class="text-gold font-bold">Why it's called "farming":</strong> The platform gives you these extra tokens as an incentive to keep your money in their pool. They do this because the more liquidity they have, the more traders will use their exchange.
            </span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Header Stats Row -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div
        class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/90 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4">
        <div
          class="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <div>
          <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-0.5">Total Farms
          </div>
          <div class="text-[16px] md:text-[20px] font-black text-white leading-none">{{topStats.activeFarms.value}}</div>
        </div>
      </div>
      <div
        class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/90 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4">
        <div
          class="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
          <svg class="w-5 h-5 md:w-6 md:h-6 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
            stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-0.5">Total TVL
          </div>
          <div class="text-[16px] md:text-[20px] font-black text-gold leading-none">{{topStats.tvl.value}}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Quick Stats -->
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
    <div
      class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4 hover:border-gold/30 transition-colors cursor-pointer">
      <div
        class="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
        <svg class="w-4 h-4 md:w-5 md:h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
          stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <div>
        <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Total Value
          Locked</div>
        <div class="text-[14px] md:text-[18px] font-black text-white leading-none mb-1">{{topStats.tvl.value}}</div>
      </div>
    </div>
    <div
      class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4 hover:border-gold/30 transition-colors cursor-pointer">
      <div
        class="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
        <svg class="w-4 h-4 md:w-5 md:h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
          stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      </div>
      <div>
        <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Active Farms
        </div>
        <div class="text-[14px] md:text-[18px] font-black text-white leading-none mb-1">{{topStats.activeFarms.value}}
        </div>
      </div>
    </div>
    <div
      class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4 hover:border-gold/30 transition-colors cursor-pointer">
      <div
        class="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
        <svg class="w-4 h-4 md:w-5 md:h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
          stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div>
        <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Rewards Paid
        </div>
        <div class="text-[14px] md:text-[18px] font-black text-white leading-none mb-1">{{topStats.rewardsPaid.value}}
        </div>
      </div>
    </div>
    <div
      class="card p-3 md:p-5 rounded-2xl bg-[#0c0c0c]/80 border border-white/[0.08] backdrop-blur-xl flex items-center gap-3 md:gap-4 hover:border-gold/30 transition-colors cursor-pointer">
      <div
        class="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full border border-gold/30 flex items-center justify-center bg-gold/5">
        <svg class="w-4 h-4 md:w-5 md:h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"
          stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      </div>
      <div>
        <div class="text-[9px] md:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1">Average APR</div>
        <div class="text-[14px] md:text-[18px] font-black text-white leading-none mb-1">{{topStats.avgApr.value}}</div>
      </div>
    </div>
  </div>


  <!-- Farms Table -->
  <div
    class="card rounded-[20px] bg-[#0c0c0c]/90 border border-white/[0.08] backdrop-blur-xl shadow-xl overflow-hidden">
    <div class="overflow-x-auto w-full">
      <table class="w-full text-left border-collapse min-w-[900px]">
        <thead>
          <tr class="border-b border-white/[0.05]">
            <th class="py-4 px-4 sm:px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">Farm</th>
            <th
              class="py-4 px-4 sm:px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">
              Earned</th>
            <th class="py-4 px-4 sm:px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">APR</th>
            <th class="py-4 px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">
              Liquidity</th>

            <th class="py-4 px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">
              Available</th>
            <th class="py-4 px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider whitespace-nowrap">
              Staked</th>
            <th
              class="py-4 px-4 sm:px-6 text-[11px] font-bold text-text-muted uppercase tracking-wider text-right pr-4 sm:pr-12 whitespace-nowrap">
              Action</th>
          </tr>
        </thead>
      <tbody>
        @if (paginatedFarms.length === 0) {
        <tr>
          <td colspan="8" class="py-12 text-center text-text-muted text-[13px]">No farms found matching your search.
          </td>
        </tr>
        }
        @for (farm of paginatedFarms; track farm.id) {

        <!-- Main Row -->
        <tr class="border-b border-white/[0.05] hover:bg-white/[0.02] transition-colors group"
          [ngClass]="expandedRow === farm.id ? 'bg-white/[0.02]' : ''">
          <td class="py-4 px-4 sm:px-6">
            <div class="flex items-center gap-2 sm:gap-4">
              <div class="flex -space-x-2 relative">
                <span
                  class="w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] sm:text-[13px] font-bold text-white z-10 border-2 border-[#0c0c0c] shadow-lg overflow-hidden"
                  [style.background]="farm.c1">
                  <img *ngIf="farm.token0Logo" [src]="farm.token0Logo" class="w-full h-full object-cover">
                  <span *ngIf="!farm.token0Logo">{{farm.token1}}</span>
                </span>
                <span
                  class="w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] sm:text-[13px] font-bold text-black border-2 border-[#0c0c0c] shadow-lg overflow-hidden"
                  [style.background]="farm.c2">
                  <img *ngIf="farm.token1Logo" [src]="farm.token1Logo" class="w-full h-full object-cover">
                  <span *ngIf="!farm.token1Logo">{{farm.token2}}</span>
                </span>
              </div>
              <div>
                <span class="text-[13px] sm:text-[14px] font-bold text-white block">{{farm.pair}}</span>
                <div class="flex flex-wrap gap-1 mt-1 max-w-[120px] sm:max-w-none">
                  @for (tag of farm.tags; track tag) {
                  <span
                    class="px-1 sm:px-1.5 py-0.5 rounded text-[8px] sm:text-[9px] font-extrabold uppercase shadow-sm flex items-center gap-0.5 whitespace-nowrap"
                    [ngClass]="tag === 'Hot' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 
                                 tag === 'Featured' ? 'bg-gold/10 text-gold border border-gold/20' : 
                                 tag === 'High APR' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                                 'bg-white/5 text-text-muted border border-white/10'">
                    @if (tag === 'Hot') {<svg class="w-2 sm:w-2.5 h-2 sm:h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd"
                        d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                        clip-rule="evenodd"></path>
                    </svg>}
                    @if (tag === 'Featured') {<svg class="w-2 sm:w-2.5 h-2 sm:h-2.5" fill="currentColor"
                      viewBox="0 0 20 20">
                      <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z">
                      </path>
                    </svg>}
                    {{tag}}
                  </span>
                  }
                </div>
              </div>
            </div>
          </td>
          <td class="py-4 px-4 sm:px-6 whitespace-nowrap">
            <div class="text-[13px] sm:text-[14px] font-bold text-white">{{farm.earned}}</div>
            <div class="text-[10px] sm:text-[11px] text-text-dim">$0</div>
          </td>
          <td class="py-4 px-4 sm:px-6 whitespace-nowrap">
            <div class="flex items-center gap-1 sm:gap-1.5">
              <span class="text-[13px] sm:text-[14px] font-black text-white">{{farm.apr}}</span>
            </div>
            <div class="text-[10px] sm:text-[11px] font-bold text-[#20d489]">{{farm.aprChange}}</div>
          </td>
          <td class="py-4 px-6 text-[14px] font-bold text-white whitespace-nowrap">{{farm.liquidity}}</td>

          <td class="py-4 px-6 whitespace-nowrap">
            <div class="text-[14px] font-bold text-white">{{farm.available}}</div>
            <div class="text-[11px] text-text-dim">$0</div>
          </td>
          <td class="py-4 px-6 whitespace-nowrap">
            <div class="text-[14px] font-bold text-white">{{farm.staked}}</div>
            <div class="text-[11px] text-text-dim">$0</div>
          </td>
          <td class="py-4 px-4 sm:px-6 text-right whitespace-nowrap">
            <div class="flex items-center justify-end gap-1.5 sm:gap-2">
              <ng-container *ngIf="getFarmPositions(farm) as mainPos">
                @if (!state.isWalletConnected() || !mainPos.hasPositions) {
                  <button [routerLink]="['/liquidity/new']" [queryParams]="{token1: farm.token0Symbol, token2: farm.token1Symbol, fee: farm.feeTier}"
                    class="px-3 sm:px-5 py-1.5 sm:py-2 bg-[#0c0c0c] hover:bg-white/5 border border-white/10 text-white rounded-lg text-[11px] sm:text-[12px] font-extrabold transition-colors">
                    Get LP
                  </button>
                } @else {
                  <button (click)="openStakeModal(farm)" [disabled]="isFetchingModal === farm.id"
                    class="px-3 sm:px-5 py-1.5 sm:py-2 bg-gold/10 hover:bg-gold border border-gold text-gold hover:text-black rounded-lg text-[11px] sm:text-[12px] font-extrabold transition-colors shadow-[0_0_10px_rgba(245,183,59,0.1)] disabled:opacity-50">
                    @if (isFetchingModal === farm.id) {
                      <svg class="animate-spin h-4 w-4 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    } @else {
                      Stake
                    }
                  </button>
                }
              </ng-container>
              <button (click)="toggleRow(farm.id)"
                class="w-7 h-7 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-[#0c0c0c] hover:bg-white/5 border border-white/10 text-white transition-all shrink-0"
                [class.rotate-180]="expandedRow === farm.id">
                <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </td>
        </tr>

        <!-- Expanded Accordion Row -->
        @if (expandedRow === farm.id) {
        <tr class="bg-[#050505] border-b border-white/[0.05]">
          <td colspan="8" class="p-0">
            <ng-container *ngIf="getFarmPositions(farm) as pos">
              <div class="p-6">
                <!-- IF NO POSITIONS -->
                @if (!pos.hasPositions) {
                <div class="flex flex-col items-center justify-center py-4">
                  <div class="text-text-muted text-[13px] mb-4">You don't have any Liquidity Positions for this pool.
                  </div>
                  <button [routerLink]="['/liquidity/new']" [queryParams]="{token1: farm.token0Symbol, token2: farm.token1Symbol, fee: farm.feeTier}"
                    class="px-8 py-3 rounded-xl bg-gold text-black hover:bg-gold/90 font-extrabold text-[13px] transition-colors shadow-[0_0_15px_rgba(245,183,59,0.2)]">
                    Add Liquidity
                  </button>
                </div>
                }
                <!-- IF HAS POSITIONS -->
                @else {
                <div class="flex flex-col lg:flex-row items-center justify-between gap-6">
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-8 xl:gap-16 w-full lg:w-auto text-left">
                    <div>
                      <div class="text-[11px] text-text-muted mb-1.5">LP Rewards</div>
                      <div class="flex items-center gap-1.5">
                        <div
                          class="w-5 h-5 rounded-full bg-[#D6A848] text-black font-bold flex items-center justify-center text-[10px]">
                          D</div>
                        <span class="text-[14px] font-bold text-white">DEX</span>
                      </div>
                    </div>
                    <div>
                      <div class="text-[11px] text-text-muted mb-1.5">Daily APR</div>
                      <div class="text-[14px] font-bold text-white">{{ (farm.rawApr / 365).toFixed(3) }}%</div>
                    </div>
                    <div>
                      <div class="text-[11px] text-text-muted mb-1.5">Performance Fee</div>
                      <div class="text-[14px] font-bold text-white">2%</div>
                    </div>
                    <div>
                      <div class="text-[11px] text-text-muted mb-1.5">Harvest Lockup</div>
                      <div class="text-[14px] font-bold text-[#20d489]">None</div>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-3 w-full lg:w-auto mt-6 lg:mt-0">
                    <button [routerLink]="['/liquidity/new']" [queryParams]="{token1: farm.token0Symbol, token2: farm.token1Symbol, fee: farm.feeTier}"
                      class="px-6 py-2.5 rounded-xl border border-gold text-gold hover:bg-gold hover:text-black font-bold text-[12.5px] transition-colors flex-1 sm:flex-none text-center whitespace-nowrap">
                      Get LP
                    </button>

                    @if (pos.available.length > 0) {
                    <button (click)="openStakeModal(farm)" [disabled]="isFetchingModal === farm.id"
                      class="px-6 py-2.5 rounded-xl bg-gold text-black hover:bg-gold/90 font-bold text-[12.5px] transition-colors shadow-[0_0_15px_rgba(245,183,59,0.2)] flex-1 sm:flex-none text-center whitespace-nowrap disabled:opacity-50">
                      @if (isFetchingModal === farm.id) {
                        <svg class="animate-spin h-4 w-4 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      } @else {
                        Stake LP
                      }
                    </button>
                    } @else if (pos.staked.length > 0) {
                    <button (click)="unstakeLP(farm)" [disabled]="isStaking"
                      class="px-6 py-2.5 rounded-xl bg-red-500/20 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/50 font-bold text-[12.5px] transition-all flex-1 sm:flex-none text-center whitespace-nowrap disabled:opacity-50">
                      @if (isStaking) {
                      <svg class="animate-spin h-4 w-4 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                      </svg>
                      } @else {
                      Unstake LP
                      }
                    </button>
                    }

                    <a [href]="getBlockExplorerUrl(farm.id)" target="_blank"
                      class="flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl text-text-muted hover:text-white font-bold text-[12px] transition-colors whitespace-nowrap w-full sm:w-auto mt-2 sm:mt-0">
                      View Contract <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
                }
              </div>
            </ng-container>
          </td>
        </tr>
        }

        }
      </tbody>
    </table>
    </div>

    <!-- Pagination Footer -->
    <div class="flex flex-col sm:flex-row items-center justify-between py-4 px-6 border-t border-white/[0.05]">
      <div class="text-[12px] font-bold text-text-muted mb-4 sm:mb-0">
        Showing {{ totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage *
        itemsPerPage, totalRecords) }} of {{ totalRecords }} farms
      </div>

      <div class="flex flex-wrap items-center justify-center gap-2 mb-4 sm:mb-0" *ngIf="totalPages > 1">
        <button (click)="prevPage()" [disabled]="currentPage === 1"
          class="w-7 h-7 flex items-center justify-center rounded bg-white/[0.03] text-text-dim hover:text-white disabled:opacity-50 transition-colors"><svg
            class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg></button>

        @for (page of paginationPages; track $index) {
        @if (page === '...') {
        <span class="text-text-dim text-[12px] font-bold mx-1">...</span>
        } @else {
        <button (click)="goToPage(page)"
          class="w-7 h-7 flex items-center justify-center rounded font-bold text-[12px] transition-colors"
          [ngClass]="page === currentPage ? 'bg-gold text-black font-extrabold shadow-[0_0_10px_rgba(245,183,59,0.2)]' : 'bg-white/[0.03] text-text-muted hover:text-white hover:bg-white/[0.05]'">
          {{ page }}
        </button>
        }
        }

        <button (click)="nextPage()" [disabled]="currentPage === totalPages"
          class="w-7 h-7 flex items-center justify-center rounded bg-white/[0.03] text-text-dim hover:text-white disabled:opacity-50 transition-colors"><svg
            class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg></button>
      </div>

      <div class="hidden md:flex items-center gap-2 text-[12px] font-bold text-text-muted">
        Rows per page:
        <div class="relative">
          <select [(ngModel)]="itemsPerPage" (ngModelChange)="currentPage = 1; expandedRow = null; fetchFarms()"
            class="bg-[#0c0c0c] border border-white/10 rounded py-1 pl-2 pr-6 outline-none appearance-none cursor-pointer">
            <option [ngValue]="10">10</option>
            <option [ngValue]="20">20</option>
            <option [ngValue]="50">50</option>
          </select>
          <svg class="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gold pointer-events-none" fill="none"
            viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  </div>

</section>

<!-- NFT Selection Modal -->
@if (showNftModal) {
<div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
  <!-- Prevent backdrop close when staking -->
  <div class="absolute inset-0" (click)="!isStaking && closeStakeModal()"></div>
  <div
    class="bg-[#0c0c0c] border border-white/[0.08] rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden animate-slide-up relative z-10">
    <!-- Header -->
    <div class="flex items-center justify-between p-6 border-b border-white/[0.05]">
      <div>
        <h3 class="text-xl font-bold text-white">Select LP NFT to Stake</h3>
        <p class="text-[13px] text-text-muted mt-1">Choose a DEX Position NFT to deposit into the farm.</p>
      </div>
      <button (click)="closeStakeModal()"
        class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-text-muted hover:text-white transition-colors">
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Body / Cards -->
    <div class="p-6 min-h-[60vh] max-h-[80vh] overflow-y-auto custom-scrollbar">
      <div class="flex flex-wrap gap-6 justify-start">
        @for (nft of availableToStake; track nft.tokenId) {

        <!-- Premium LP NFT Card -->
        <div class="relative w-[160px] h-[220px] rounded-[20px] cursor-pointer group hover:-translate-y-2 transition-all duration-300"
          [ngClass]="isStaking ? 'opacity-50 pointer-events-none' : ''" (click)="stakeSelectedToken(nft.tokenId)">
          
          <!-- Outer Glow on Hover -->
          <div class="absolute inset-0 bg-gold/20 blur-[20px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <!-- Card Inner -->
          <div class="relative w-full h-full rounded-[20px] bg-[#0c0c0c] overflow-hidden border border-white/10 group-hover:border-gold/30 transition-colors shadow-xl">
            
            <!-- Abstract Graphic Background -->
            <div class="absolute inset-0 bg-gradient-to-br from-[#111] to-[#050505]"></div>
            <div class="absolute -top-10 -right-10 w-24 h-24 bg-gold/10 blur-[30px] rounded-full"></div>
            <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 blur-[40px] rounded-full"></div>
            
            <!-- Liquid/Wave SVG -->
            <svg class="absolute bottom-0 left-0 w-full text-gold/10 group-hover:text-gold/20 transition-colors duration-500" viewBox="0 0 160 100" fill="currentColor">
              <path d="M0,50 C40,80 80,20 160,50 L160,100 L0,100 Z" />
            </svg>

            <!-- Card Content -->
            <div class="relative h-full p-4 flex flex-col">
              
              <!-- Header -->
              <div class="flex justify-between items-start mb-2">
                <div class="flex -space-x-1.5">
                  <div class="w-6 h-6 rounded-full bg-[#111] border border-white/10 flex items-center justify-center text-[10px] font-black text-white z-10 overflow-hidden" [style.backgroundColor]="selectedFarmForStake.c1">
                    <img *ngIf="selectedFarmForStake.token0Logo" [src]="selectedFarmForStake.token0Logo" class="w-full h-full object-cover">
                    <span *ngIf="!selectedFarmForStake.token0Logo">{{selectedFarmForStake.token1}}</span>
                  </div>
                  <div class="w-6 h-6 rounded-full bg-[#222] border border-white/10 flex items-center justify-center text-[10px] font-black text-white overflow-hidden" [style.backgroundColor]="selectedFarmForStake.c2">
                    <img *ngIf="selectedFarmForStake.token1Logo" [src]="selectedFarmForStake.token1Logo" class="w-full h-full object-cover">
                    <span *ngIf="!selectedFarmForStake.token1Logo">{{selectedFarmForStake.token2}}</span>
                  </div>
                </div>
                <div class="text-[9px] font-mono font-bold text-gold bg-gold/10 px-1.5 py-0.5 rounded-[4px] border border-gold/20">#{{nft.tokenId}}</div>
              </div>

              <!-- Titles -->
              <div class="mt-2">
                <div class="text-white font-black text-[13px] tracking-wide">{{selectedFarmForStake.token0Symbol || 'UNK'}}/{{selectedFarmForStake.token1Symbol || 'UNK'}}</div>
                <div class="text-text-muted font-bold text-[9px] uppercase tracking-wider">V3 Liquidity</div>
              </div>

              <!-- Stats Box -->
              <div class="mt-auto bg-black/40 border border-white/5 rounded-xl p-2.5 backdrop-blur-sm">
                <div class="flex justify-between items-center mb-1.5">
                  <span class="text-text-dim text-[8.5px] font-bold">MIN TICK</span>
                  <span class="text-white font-mono text-[9px] font-bold">{{nft.tickLower}}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-text-dim text-[8.5px] font-bold">MAX TICK</span>
                  <span class="text-white font-mono text-[9px] font-bold">{{nft.tickUpper}}</span>
                </div>
              </div>

            </div>

            <!-- Hover Action Overlay -->
            @if (isStaking && stakingTokenId === nft.tokenId) {
              <div class="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md z-30">
                <div class="flex flex-col items-center gap-2">
                  <svg class="w-6 h-6 text-gold animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="text-gold text-[10px] font-black uppercase tracking-wider">Staking...</span>
                </div>
              </div>
            } @else {
              <div class="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center z-20">
                <div class="bg-gold text-black px-5 py-2 rounded-xl font-black text-[12px] shadow-[0_0_20px_rgba(245,183,59,0.5)] transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  Stake NFT
                </div>
              </div>
            }
          </div>
        </div>
        }
      </div>
      @if (availableToStake.length === 0) {
      <div class="py-12 text-center text-text-muted">No unstaked positions available.</div>
      }
    </div>
  </div>
</div>
}